import React, { useEffect, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router';
import { useSession } from '@battleops/client/src/context/SessionContext';
import { useApprovals } from '@battleops/client/src/data/useApprovals';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useProjects } from '@battleops/client/src/context/ProjectContext';
import { useTimeBlocks } from '@battleops/client/src/context/TimeBlockContext';
import { useSections } from '@battleops/client/src/context/SectionContext';
import { useDepartments } from '@battleops/client/src/context/DepartmentContext';
import { useNotes } from '@battleops/client/src/context/NoteContext';
import { useMilestones } from '@battleops/client/src/context/MilestoneContext';
import { useLocalDoc, useLocalCollection } from '@battleops/client/src/data/useLocal';
import { ensureWeekRolled } from '@battleops/client/src/data/week';
import {
  selectDayPlan, localToday, hasDayPlan, weekKey, outcomeTaskIds, outcomeProgress, carriedDays, formatDurationShort, formatDuration,
  agenda, dayReports, planAdherence, completionStreak, hoursByWeek, computeTotalTaskTime, localDateStr,
  jobBoard, planTimeline, deptRole, deptUpNext,
} from '@battleops/shared';
import Icon from '../components/Icon';
import DeskPanel, { useHomeDepartment, weekOf } from '../components/DeskPanel';
import { AllHero } from '../components/dept/ScoreCard';
import DueNow from '../components/DueNow';
import { ServerNeeds } from '../components/ServerCard';
import { useAllScores } from '@battleops/client/src/data/useDeptGoals';
import Kpi from '../components/Kpi';
import DevDashboard from '../components/dept/DevDashboard';
import FieldDashboard from '../components/dept/FieldDashboard';
import WeekStrip from '../components/WeekStrip';
import SalesDashboard from '../components/dept/SalesDashboard';
import PipelineBoard from '../components/dept/PipelineBoard';
import { BoardView } from './Board';
import { departmentBoardSettings } from '@battleops/shared';
import CounterTiles from '../components/CounterTiles';
import { AddLine } from '../components/TaskDetail';
import { StackedBars, HBars, SplitBar, Legend, SERIES, OTHER } from '../components/charts';
import { useStartTask, isoAddDays, hhmm } from '../lib/actions';
import { useQuickAdd } from '../lib/quickadd';
import { distinctColors } from '../lib/colors';
import { useNow, usePanel, useTaskModal } from '../hooks';
import t from './Today.module.css';
import s from './Home.module.css';
import { useRoutines } from '@battleops/client/src/context/ProcessContext';
import { openRoutineMode } from '../lib/routineMode';
import { formatClockMin, msToMin, routineStats, addDaysStr as addD, localToday as today0 } from '@battleops/shared';

const H = 3600000;
const hrs = (ms) => (ms >= H ? `${(ms / H).toFixed(1)}h` : formatDurationShort(ms));
const shortDay = (date) => new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const TARGET_H = 8;
const TAB_KEY = 'ops.web.homeTab';
const readTab = () => { try { return localStorage.getItem(TAB_KEY); } catch { return null; } };

// Home, in two tabs. Dashboard: what's next and the numbers that matter. Battle Plan: the work itself — the next two
// weeks by day, today's plan, and every job ranked by how much it needs you (shared/battleplan.js).
// Home (was): what's next, then the numbers that matter, then the working lists. Up next = the running task or the plan's
// first, big, with the next few and the next appointment; four KPI tiles with 14-day trends; the last 14 days by job,
// today's clock split, where the time went, plan hit rate + streak; the plan, carried, done, calendar and the week.
export default function Home() {
  const { identity } = useSession();
  const { tasks, clockSessions, clockedIn, activeTaskId, timer, loaded, setTaskDone, completeTask } = useTasks();
  const { projects } = useProjects();
  const { getBlocksForDate } = useTimeBlocks();
  const { sections } = useSections();
  const { rawDepartments, departmentMode, setCurrentDepartment } = useDepartments();
  const homeDept = useHomeDepartment();
  const allScores = useAllScores();   // department mode on, All picked: the overall + every department (the phone's AllHero)   // a department picked: Home is its own (the score card + its cards)
  const { notes } = useNotes();
  const { milestones } = useMilestones();
  const navigate = useNavigate();
  const panel = usePanel();
  const details = useTaskModal();
  const start = useStartTask();
  const quickAdd = useQuickAdd();
  const now = useNow(1000);
  const [params, setParams] = useSearchParams();
  // Board is a tab on a department's Home (Lj, Oct 16: "boards like tabs instead of its own screen"): Sales' = the
  // pipeline; any other department with its board on = its task board.
  const boardTab = homeDept ? (deptRole(homeDept) === 'sales' ? 'pipeline' : (() => { const st = departmentBoardSettings(homeDept); return st.on && !st.hidden ? 'board' : null; })()) : null;
  const want = params.get('tab') || readTab();
  const tab = want === 'plan' ? 'plan' : want === 'board' && boardTab ? 'board' : 'dash';
  const setTab = (k) => { try { localStorage.setItem(TAB_KEY, k); } catch { /* per-browser nicety */ } setParams(k === 'dash' ? {} : { tab: k }, { replace: true }); };

  const today = localToday();
  const wk = weekKey(today);
  const week = useLocalDoc('weekPlans', wk);
  const blocks = getBlocksForDate(today);
  const calEvents = useLocalCollection('calendarEvents');
  const { waiting: approvals } = useApprovals({ online: true, live: false });   // the server's list when it answers
  useEffect(() => { ensureWeekRolled(wk).catch(() => {}); }, [wk]);

  // Plan
  const planExists = hasDayPlan({ tasks, blocks, today, projects });
  const oIds = useMemo(() => outcomeTaskIds(week), [week]);
  const plan = useMemo(() => selectDayPlan({ tasks, blocks, sections: sections || [], today, outcomeTaskIds: oIds, projects }), [tasks, blocks, sections, today, oIds, projects]);
  const fresh = plan.filter((x) => carriedDays(x, today) === 0);
  const carried = plan.filter((x) => carriedDays(x, today) > 0);
  const doneToday = useMemo(() => tasks.filter((x) => x.status === 'done' && !x.parentId && x.completedAt && localDateStr(x.completedAt) === today)
    .sort((a, b) => (a.completedAt || 0) - (b.completedAt || 0)), [tasks, today]);
  const running = activeTaskId ? tasks.find((x) => x.id === activeTaskId) : null;
  const next = running || fresh[0] || carried[0] || null;
  const queue = plan.filter((x) => x.id !== next?.id).slice(0, 4);

  // Calendar: what's coming today and tomorrow
  const upcoming = useMemo(() => agenda({ from: today, to: isoAddDays(today, 1), today, events: calEvents })
    .flatMap((d) => d.events || []).filter((e) => e.status === 'planned' && (e.start ? e.start > Date.now() - 15 * 60000 : d0(e) === today)), [calEvents, today]);
  const nextEvent = upcoming.find((e) => e.start) || null;

  // Numbers (recomputed every 5 minutes, and when data changes)
  const bucket = Math.floor(now / 300000);
  const sessions = clockSessions || [];
  const reports = useMemo(() => dayReports({ tasks, sessions, projects, notes, departments: rawDepartments, days: 30, now: Date.now() }), [tasks, sessions, projects, notes, rawDepartments, bucket]);
  const days = useMemo(() => [...reports.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([date, r]) => ({ date, r })), [reports]);
  const last14 = days.slice(-14);
  const todayR = reports.get(today);
  const weeks = useMemo(() => hoursByWeek({ sessions, weeks: 8, now: Date.now() }), [sessions, bucket]);
  const adherence = useMemo(() => planAdherence(new Map(last14.map((d) => [d.date, d.r]))), [last14]);
  const streak = useMemo(() => completionStreak(tasks), [tasks]);
  const projById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const allBlocks = useLocalCollection('timeBlocks');
  const board = useMemo(() => jobBoard({ projects, tasks, blocks: allBlocks || [], today }), [projects, tasks, allBlocks, today]);
  const timeline = useMemo(() => planTimeline({ board, tasks, today, days: 14 }), [board, tasks, today]);

  const clocked = todayR?.clock.clockedMs || 0;
  const worked = todayR?.time.workedMs || 0;
  const focus = clocked ? worked / clocked : 0;
  const avgClocked = last14.slice(0, -1).filter((d) => d.r.clock.clockedMs > 0);
  const avg = avgClocked.length ? avgClocked.reduce((a, d) => a + d.r.clock.clockedMs, 0) / avgClocked.length : 0;
  const thisWeek = weeks[weeks.length - 1]?.totalMs || 0;
  const lastWeek = weeks[weeks.length - 2]?.totalMs || 0;
  const doneCount = todayR?.tasks.doneCount || 0;
  const hit = adherence.filter((a) => a.rate != null);
  const hitRate = hit.length ? Math.round(hit.reduce((a, x) => a + x.rate, 0) / hit.length) : null;

  // The last 14 days by job: top 5 jobs keep their colour, the rest fold into Other.
  const top = useMemo(() => {
    const tot = new Map();
    for (const { r } of last14) for (const [pid, ms] of Object.entries(r.clock.byProject || {})) if (pid !== 'none') tot.set(pid, (tot.get(pid) || 0) + ms);
    return [...tot.entries()].sort((a, b) => b[1] - a[1]);
  }, [last14]);
  const keep = new Set(top.slice(0, 5).map(([pid]) => pid));
  // A job keeps its own colour unless a busier job already has it — then the next free series colour (identity must
  // never collide in one chart).
  const colors = useMemo(() => distinctColors(top.slice(0, 7).map(([pid]) => pid), (pid) => projById.get(pid)?.color), [top, projById]);
  const colorOf = (pid) => colors.get(pid) || OTHER;
  const bars = last14.map(({ date, r }) => {
    const by = r.clock.byProject || {};
    const segs = []; let other = 0;
    for (const [pid, ms] of Object.entries(by)) {
      if (keep.has(pid)) segs.push({ id: pid, label: projById.get(pid)?.name || 'Job', value: ms / H, color: colorOf(pid) });
      else other += ms;
    }
    if (other) segs.push({ id: 'other', label: 'Other / no job', value: other / H, color: OTHER });
    segs.sort((a, b) => [...keep].indexOf(a.id) - [...keep].indexOf(b.id));
    return { key: date, label: String(new Date(`${date}T12:00:00`).getDate()), tip: shortDay(date), segments: segs };
  });
  const legend = [...[...keep].map((pid) => ({ id: pid, label: projById.get(pid)?.name || 'Job', color: colorOf(pid) })), ...(top.length > 5 || bars.some((b) => b.segments.some((g) => g.id === 'other')) ? [{ id: 'other', label: 'Other / no job', color: OTHER }] : [])];
  const whereRows = top.slice(0, 7).map(([pid, ms]) => ({ id: pid, label: `${projById.get(pid)?.icon || ''} ${projById.get(pid)?.name || 'Job'}`.trim(), value: ms, color: colorOf(pid) }));

  const setup = (todayR?.time.setupMs || 0) + (todayR?.time.cleanupMs || 0);
  const travel = todayR?.travel.ms || 0;
  const breaks = todayR?.breaks.ms || 0;
  const split = [
    { id: 'task', label: 'On task', value: worked, color: SERIES[3] },
    { id: 'setup', label: 'Setup + cleanup', value: setup, color: SERIES[2] },
    { id: 'travel', label: 'Travel', value: travel, color: SERIES[1] },
    { id: 'break', label: 'Breaks', value: breaks, color: SERIES[4] },
    { id: 'rest', label: 'Clocked, no task', value: Math.max(0, clocked - worked - setup - travel), color: OTHER },
  ];

  const spark = (fn) => last14.map(({ date, r }) => ({ key: date, label: shortDay(date), value: fn(r) }));
  const hour = new Date(now).getHours();
  const hello = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const first = (identity?.name || '').split(' ')[0];
  const outcomes = week?.outcomes || [];

  if (!loaded) return <div className="page faint">Loading…</div>;

  const pOf = (x) => (x?.projectId ? projById.get(x.projectId) : null);
  const row = (x, n) => {
    const p = pOf(x);
    const run = activeTaskId === x.id;
    const cd = carriedDays(x, today);
    return (
      <div key={x.id} className={t.task} data-running={run || undefined} style={{ '--pc': p?.color || 'var(--c-border-strong)' }}
        onClick={() => panel.open('task', x.id)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') panel.open('task', x.id); }}>
        <span className={t.n}>{n}</span>
        <button className={t.check} onClick={(e) => { e.stopPropagation(); setTaskDone(x.id, true); }} title="Done" aria-label="Mark done" />
        <div className={t.tBody}>
          <div className={t.tTitle}>{x.title}</div>
          <div className={t.tMeta}>
            {p ? <span><span className="dot" style={{ background: p.color }} /> {p.name}</span> : null}
            {x.estimatedMinutes ? <span>~{x.estimatedMinutes}m</span> : null}
            {cd ? <span style={{ color: 'var(--c-warning)' }}>carried {cd}d</span> : null}
            {oIds.has(x.id) ? <span style={{ color: 'var(--c-gold)' }}>◎ week</span> : null}
          </div>
        </div>
        {run ? <span className={s.runPill}><span className={s.pulse} />Running</span>
          : <button className={s.startBtn} onClick={(e) => { e.stopPropagation(); start(x); }}><Icon name="play" size={12} />Start</button>}
      </div>
    );
  };

  // a department's Home: the card is that department's — its planned tasks only; a task running in another
  // department becomes a small "Clocked on …" line (Lj, Oct 11)
  const deptUp = homeDept ? deptUpNext(homeDept, { plan: [...fresh, ...carried], running, tasks, projects, departments: rawDepartments || [], departmentMode }) : null;
  const hNext = deptUp ? deptUp.next : next;
  const hRunning = deptUp ? deptUp.running : running;
  const hQueue = deptUp ? deptUp.queue : queue;
  const nextP = pOf(hNext);
  const nextMs = hNext ? (timer?.taskId === hNext.id ? computeTotalTaskTime(hNext, timer, now) : hNext.timeSpent || 0) : 0;
  const untilEvent = nextEvent?.start ? nextEvent.start - now : null;

  // Up next / Working on — the running task with its live timer, then the plan (shared by All and a department's Home)
  const upNextHero = (
        <section className={s.hero} style={{ '--pc': nextP?.color || 'var(--c-accent)' }}>
          {deptUp?.elsewhere ? (
            <button className={s.elsewhere} onClick={() => details.open(deptUp.elsewhere.task.id)}>
              <span className={s.pulse} />Clocked on {deptUp.elsewhere.project?.name || deptUp.elsewhere.task.title} · {deptUp.elsewhere.dept?.name || 'another department'}
            </button>
          ) : null}
          <div className={s.kicker}>{hRunning ? <><span className={s.pulse} />Working on</> : deptUp ? `Up next · ${homeDept.name}` : 'Up next'}</div>
          {hNext ? (
            <>
              <button className={s.heroTitle} onClick={() => details.open(hNext.id)}>{hNext.title}</button>
              <div className={s.heroMeta}>
                {nextP ? <span><span className="dot" style={{ background: nextP.color }} /> {nextP.icon} {nextP.name}</span> : <span>No job</span>}
                {hNext.estimatedMinutes ? <span>~{hNext.estimatedMinutes}m planned</span> : null}
                {nextMs ? <span className="num">{formatDurationShort(nextMs)} on it{hRunning ? ' so far' : ''}</span> : null}
                {carriedDays(hNext, today) ? <span style={{ color: 'var(--c-warning)' }}>carried {carriedDays(hNext, today)}d</span> : null}
              </div>
              <div className={s.heroActs}>
                {hRunning ? <span className={s.heroTimer} role="timer" aria-label={`Running, ${formatDurationShort(nextMs)} on it`}><span className={s.pulse} />{formatDuration(nextMs)}<small>running</small></span>
                  : <button className={s.heroStart} onClick={() => start(hNext)}><Icon name="play" size={16} />Start{clockedIn ? '' : ' · clocks you in'}</button>}
                <button className="btn" onClick={() => (hRunning || hNext.id === activeTaskId ? completeTask(hNext.id) : setTaskDone(hNext.id, true))} title={hRunning ? 'Finish the running task (its time is banked, the timer clears)' : 'Mark done'}><Icon name="check" size={15} />Done</button>
                <button className="btn ghost" onClick={() => details.open(hNext.id)}><Icon name="maximize-2" size={15} />Open</button>
              </div>
            </>
          ) : (
            <div className={s.heroEmpty}>{deptUp ? `Nothing planned for ${homeDept.name} today.` : planExists ? 'Plan’s clear — nice.' : 'Nothing planned yet.'} <button className={s.link} onClick={() => navigate('/plan')}>{planExists ? 'Pull something in' : 'Plan your day'} →</button></div>
          )}
          {hQueue.length || deptUp?.hidden ? (
            <div className={s.queue}>
              {hQueue.length ? <div className={s.qHead}>Then</div> : null}
              {hQueue.map((x) => {
                const p = pOf(x);
                return (
                  <button key={x.id} className={s.qRow} onClick={() => panel.open('task', x.id)}>
                    <span className="dot" style={{ background: p?.color || 'var(--c-text-faint)' }} /><span className={s.qTitle}>{x.title}</span>
                    {x.estimatedMinutes ? <span className="faint num">~{x.estimatedMinutes}m</span> : null}
                  </button>
                );
              })}
              {deptUp?.hidden ? <div className={s.qMore}>+{deptUp.hidden} planned in other departments</div> : null}
            </div>
          ) : null}
          {nextEvent ? (
            <button className={s.nextEvent} onClick={() => panel.open('event', nextEvent.id)}>
              <Icon name="calendar" size={15} /><span className={s.qTitle}>{nextEvent.title}</span>
              <span className="num">{hhmm(nextEvent.start)}</span>
              <span className={s.until} data-soon={untilEvent != null && untilEvent < 3600000 ? '' : undefined}>{untilEvent > 0 ? `in ${formatDurationShort(untilEvent)}` : 'now'}</span>
            </button>
          ) : null}
        </section>
  );

  return (
    <div className={s.page} data-wide={(tab === 'dash' && homeDept && deptRole(homeDept) === 'field') || undefined}>
      <header className={s.head}>
        <div>
          <div className={s.date}>{new Date(now).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</div>
          <h1 className={s.hello}>{hello}{first ? `, ${first}` : ''}</h1>
        </div>
        <div className={s.tabs} role="tablist">
          <button role="tab" aria-selected={tab === 'dash'} data-on={tab === 'dash' || undefined} onClick={() => setTab('dash')}><Icon name="bar-chart-2" size={15} />Dashboard</button>
          {boardTab ? <button role="tab" aria-selected={tab === 'board'} data-on={tab === 'board' || undefined} onClick={() => setTab('board')}><Icon name="columns" size={15} />{boardTab === 'pipeline' ? 'Pipeline' : 'Board'}</button> : null}
          <button role="tab" aria-selected={tab === 'plan'} data-on={tab === 'plan' || undefined} onClick={() => setTab('plan')}><Icon name="target" size={15} />Battle Plan
            {fresh.length + carried.length ? <span className={s.tabCount}>{fresh.length + carried.length}</span> : null}</button>
        </div>
        <span className="spacer" />
        {approvals.length ? <Link to="/approvals" className={s.pill} data-tone="gold"><Icon name="shield" size={14} />{approvals.length} waiting for approval</Link> : null}
        <button className="btn" onClick={() => navigate('/plan')}><Icon name="list" size={15} />{planExists ? 'Plan my day' : 'Plan your day'}</button>
      </header>

      {tab === 'dash' ? <WeekStrip /> : null}{/* every Home, every department (Lj, Nov 2: "globalized") */}
      {tab === 'dash' && homeDept ? (
        /* department mode, a department picked: its score hero, then its own dashboard (Dev: the All Home's cards with
           Dev's data) — none of Home's generic numbers */
        deptRole(homeDept) === 'dev' ? <DevDashboard dept={homeDept} upNext={upNextHero} /> : deptRole(homeDept) === 'field' ? <FieldDashboard dept={homeDept} upNext={upNextHero} />
          : deptRole(homeDept) === 'sales' ? <SalesDashboard dept={homeDept} upNext={upNextHero} onBoard={() => setTab('board')} /> : <DeskPanel dept={homeDept} />
      ) : tab === 'board' && homeDept ? (
        boardTab === 'pipeline' ? <PipelineBoard dept={homeDept} /> : <BoardView scope={{ kind: 'department', id: homeDept.id }} embedded />
      ) : tab === 'dash' ? <>
      <DueNow />
      <ServerNeeds />
      {departmentMode && allScores.departments.length ? <AllHero all={allScores} subtitle={weekOf()} onPick={(id) => setCurrentDepartment(id)} /> : null}
      <CounterTiles />
      {/* ── Up next + KPIs ─────────────────────────────────────────── */}
      <div className={s.topGrid}>
        {upNextHero}

        <div className={s.kpis}>
          <Kpi label="Clocked today" value={hrs(clocked)} sub={avg ? `${clocked >= avg ? '▲' : '▼'} vs ${hrs(avg)} avg` : '14-day average builds up'} tone={avg && clocked >= avg ? 'up' : null}
            points={spark((r) => r.clock.clockedMs / H)} format={(v) => `${v.toFixed(1)}h`} to="/time" />
          <Kpi label="Focus" value={clocked ? `${Math.round(focus * 100)}%` : '—'} sub={`${formatDurationShort(worked)} on tasks`} tone={clocked ? (focus >= 0.7 ? 'up' : 'down') : null}
            points={spark((r) => Math.round((r.time.focusRatio || 0) * 100))} format={(v) => `${v}%`} color="var(--c-success)" to="/insights" />
          <Kpi label="Done today" value={String(doneCount)} sub={`${fresh.length + carried.length} still on the plan`}
            points={spark((r) => r.tasks.doneCount || 0)} format={(v) => `${v} done`} color="#3F7FE6" to="/log" />
          <Kpi label="This week" value={hrs(thisWeek)} sub={lastWeek ? `last week ${hrs(lastWeek)}` : 'first week tracked'} tone={lastWeek ? (thisWeek >= lastWeek ? 'up' : null) : null}
            points={weeks.map((w) => ({ key: w.week, label: `Week of ${shortDay(w.week)}`, value: w.totalMs / H }))} format={(v) => `${v.toFixed(1)}h`} color="var(--c-gold)" to="/time" />
        </div>
      </div>

      {/* ── Charts ─────────────────────────────────────────────────── */}
      <div className={s.chartGrid}>
        <section className={s.card}>
          <div className={s.cardHead}><h2>Last 14 days</h2><span className={s.cardSub}>hours clocked, by job · dashed line = {TARGET_H} h</span></div>
          <StackedBars data={bars} height={230} target={TARGET_H} format={(v) => (v >= 1 ? `${v.toFixed(v >= 10 ? 0 : 1)}h` : `${Math.round(v * 60)}m`)} />
          <Legend items={legend} />
        </section>
        <div className={s.stack}>
          <section className={s.card}>
            <div className={s.cardHead}><h2>Today’s clock</h2><span className={s.cardSub}>{clocked ? `${hrs(clocked)} clocked` : 'Not clocked in yet'}</span></div>
            <SplitBar parts={split} format={formatDurationShort} />
          </section>
          <section className={s.card}>
            <div className={s.cardHead}><h2>Plan hit rate</h2><span className={s.cardSub}>planned tasks finished the same day · 14 days</span></div>
            <div className={s.bigRow}>
              <div><div className={s.big}>{hitRate == null ? '—' : `${hitRate}%`}</div><div className={s.bigSub}>average</div></div>
              <div><div className={s.big}>{streak}<span className={s.bigUnit}> day{streak === 1 ? '' : 's'}</span></div><div className={s.bigSub}>streak with something done</div></div>
            </div>
            <div className={s.dots} aria-label="Daily hit rate">
              {adherence.map((a) => (
                <span key={a.date} title={`${shortDay(a.date)} · ${a.rate == null ? 'nothing planned' : `${a.doneOfPlan}/${a.planned} planned done`}`}
                  data-level={a.rate == null ? 'none' : a.rate >= 80 ? 'high' : a.rate >= 50 ? 'mid' : 'low'} />
              ))}
            </div>
          </section>
        </div>
        <RoutinesCard />
        <section className={s.card}>
          <div className={s.cardHead}><h2>Where the time went</h2><span className={s.cardSub}>clocked by job · 14 days</span></div>
          {whereRows.length ? <HBars rows={whereRows} format={(ms) => hrs(ms)} /> : <div className="empty">No clocked time yet.</div>}
          <Link to="/insights" className={s.more}>Insights <Icon name="chevron-right" size={13} /></Link>
        </section>
      </div>

      </> : <>
      {/* ── Battle Plan: two weeks, today's plan, jobs ranked ─────────── */}
      <section className={`${s.card} ${s.strip}`}>
        <div className={s.cardHead}><h2>Next two weeks</h2><span className={s.cardSub}>jobs on each day · ⚑ due · dated tasks</span></div>
        <div className={s.days}>
          {timeline.map((d) => {
            const dt = new Date(`${d.date}T12:00:00`);
            const wkEnd = dt.getDay() === 0 || dt.getDay() === 6;
            return (
              <button key={d.date} className={s.day} data-today={d.date === today || undefined} data-weekend={wkEnd || undefined} onClick={() => navigate(`/plan/${d.date}`)} title={`Plan ${shortDay(d.date)}`}>
                <span className={s.dayName}>{dt.toLocaleDateString(undefined, { weekday: 'short' })}</span>
                <span className={s.dayNum}>{dt.getDate()}</span>
                <span className={s.dayJobs}>
                  {d.due.map((pid) => <span key={`d${pid}`} className={s.dueFlag} style={{ '--pc': projById.get(pid)?.color || 'var(--c-accent)' }} title={`${projById.get(pid)?.name} due`}><Icon name="flag" size={11} /></span>)}
                  {d.on.map((pid) => <span key={pid} className={s.onBar} style={{ background: projById.get(pid)?.color || 'var(--c-text-faint)' }} title={projById.get(pid)?.name} />)}
                </span>
                {d.tasks ? <span className={s.dayTasks}>{d.tasks} task{d.tasks === 1 ? '' : 's'}</span> : <span className={s.dayTasks} data-none>—</span>}
              </button>
            );
          })}
        </div>
      </section>
      <div className={s.listGrid}>
        <div>
          <section className={s.card}>
            <div className={s.cardHead}><h2>The plan</h2><span className="faint num">{fresh.length}</span></div>
            {fresh.map((x, i) => row(x, i + 1))}
            {!fresh.length ? <div className="empty">{planExists ? 'Plan’s clear.' : 'Nothing dated or carried over yet — add below or plan your day.'}</div> : null}
            <div className={t.add}><AddLine placeholder="Add to today…  (~45m, $120, !, #tag · “remind me …”)" onAdd={(v) => quickAdd(v, { date: today, allowEvent: false })} /></div>
          </section>
          {carried.length ? (
            <section className={s.card}>
              <div className={s.cardHead}><h2 style={{ color: 'var(--c-warning)' }}>Carried over</h2><span className="faint num">{carried.length}</span></div>
              {carried.map((x, i) => row(x, fresh.length + i + 1))}
            </section>
          ) : null}
        </div>
        <div>
          <section className={s.card}>
            <div className={s.cardHead}><h2>Jobs</h2><span className="faint num">{board.length}</span><span className={s.cardSub}>most important first — due dates, carried and critical work, what’s on</span></div>
            {board.map((r, i) => {
              const p = r.project;
              return (
                <div key={r.id} className={s.job} style={{ '--pc': p.color || 'var(--c-text-faint)' }}>
                  <span className={s.jobRank}>{i + 1}</span>
                  <div className={s.jobBody}>
                    <div className={s.jobTop}>
                      <Link to={`/projects/${p.id}`} className={s.jobName}>{p.icon} {p.name}</Link>
                      {r.daysLeft != null ? <span className={s.due} data-tone={r.daysLeft < 0 ? 'late' : r.daysLeft <= 3 ? 'soon' : undefined}>
                        <Icon name="flag" size={11} />{r.daysLeft < 0 ? `${-r.daysLeft}d late` : r.daysLeft === 0 ? 'today' : new Date(`${r.endDate}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span> : null}
                    </div>
                    {r.reasons.length ? <div className={s.reasons}>{r.reasons.map((x) => <span key={x}>{x}</span>)}</div> : null}
                    <div className={s.jobProg}><span style={{ width: `${r.pct}%` }} /></div>
                    <div className={s.jobMeta}><span className="num">{r.done}/{r.total} done</span>{r.estMin ? <span className="num">~{formatDurationShort(r.estMin * 60000)} left</span> : null}
                      {r.nextOn ? <span>{r.nextOn === today ? 'on today' : `next on ${shortDay(r.nextOn)}`}</span> : <span className="faint">not scheduled</span>}</div>
                    {r.next ? (
                      <div className={s.jobNext}>
                        <button className={s.qTitle} onClick={() => details.open(r.next.id)} title="Open task">→ {r.next.title}</button>
                        {activeTaskId === r.next.id ? <span className={s.runPill}><span className={s.pulse} />Running</span>
                          : <button className={s.startBtn} onClick={() => start(r.next)}><Icon name="play" size={12} />Start</button>}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {!board.length ? <div className="empty">No live jobs.</div> : null}
          </section>
          {doneToday.length ? (
            <section className={s.card}>
              <div className={s.cardHead}><h2 style={{ color: 'var(--c-success)' }}>Done today</h2><span className="faint num">{doneToday.length}</span></div>
              {doneToday.map((x) => (
                <div key={x.id} className={t.done} onClick={() => panel.open('task', x.id)} role="button" tabIndex={0}>
                  <Icon name="check" size={13} color="var(--c-success)" /><span style={{ flex: 1 }}>{x.title}</span><span className="faint num">{hhmm(x.completedAt)}</span>
                </div>
              ))}
            </section>
          ) : null}
          {upcoming.length ? (
            <section className={s.card}>
              <div className={s.cardHead}><h2>Coming up</h2><Link to="/calendar" className={s.more} style={{ margin: 0 }}>Calendar <Icon name="chevron-right" size={13} /></Link></div>
              {upcoming.slice(0, 6).map((e) => (
                <button key={e.id} className={t.done} onClick={() => panel.open('event', e.id)}>
                  <Icon name="calendar" size={13} /><span style={{ flex: 1, textAlign: 'left' }}>{e.title}</span>
                  <span className="faint num">{d0(e) !== today ? 'Tmrw ' : ''}{e.start ? hhmm(e.start) : 'All day'}</span>
                </button>
              ))}
            </section>
          ) : null}
          <section className={s.card}>
            <div className={s.cardHead}><h2>Week {wk.split('-W')[1]}</h2><span className="spacer" />
              <Link to="/week" className={s.more} style={{ margin: 0 }}>{outcomes.length ? `${outcomes.filter((o) => o.done || outcomeProgress(o, tasks, milestones).complete).length}/${outcomes.length} outcomes` : 'Set outcomes'} <Icon name="chevron-right" size={13} /></Link>
            </div>
            {outcomes.length ? outcomes.map((o) => {
              const pr = outcomeProgress(o, tasks, milestones);
              return (
                <div key={o.id} className={t.outcome}>
                  <Icon name={o.done || pr.complete ? 'check-circle' : 'target'} size={13} color={o.done || pr.complete ? 'var(--c-success)' : 'var(--c-gold)'} />
                  <span style={{ flex: 1 }}>{o.title}</span>{pr.total ? <span className="faint num">{pr.done}/{pr.total}</span> : null}
                </div>
              );
            }) : <div className="faint" style={{ fontSize: 13 }}>No outcomes set for this week yet.</div>}
          </section>
        </div>
      </div>
      </>}
    </div>
  );
}

function d0(e) { return e.date || (e.start ? localDateStr(e.start) : null); }

// Today's routines on the dashboard: each with where it stands (ready / later / going / done / missed), Start, and its
// streak; the title opens Routines.
const R_STATE = { upcoming: 'Later', due: 'Ready', running: 'Going', paused: 'Paused', done: 'Done', partial: 'Part done', skipped: 'Skipped', missed: 'Missed' };
function RoutinesCard() {
  const { today, routines, runs, startRoutine } = useRoutines();
  const list = today(Date.now());
  const streaks = useMemo(() => new Map(routines.map((r) => [r.id, routineStats(r, runs, { from: addD(today0(), -29), to: today0() }).streak.current])), [routines, runs]);
  if (!routines.length) return null;
  return (
    <section className={s.card}>
      <div className={s.cardHead}><h2><Link to="/routines">Routines today</Link></h2><span className={s.cardSub}>{list.filter((x) => ['done', 'partial'].includes(x.state)).length} of {list.length} done</span></div>
      {list.length ? list.map(({ routine: r, run, state, at }) => (
        <div key={`${r.id}${run?.n || ''}`} className="row" style={{ padding: '7px 0', borderTop: '1px solid var(--c-border)' }}>
          <span style={{ fontSize: 20 }}>{r.look.icon}</span>
          <Link to={`/routines/${r.id}`} style={{ fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</Link>
          {streaks.get(r.id) ? <span className="num" style={{ color: 'var(--c-gold)', fontSize: 12 }}>🔥 {streaks.get(r.id)}</span> : null}
          <span className="faint" style={{ fontSize: 12 }}>{R_STATE[state]}{at && ['upcoming', 'due'].includes(state) ? ` · ${formatClockMin(msToMin(at))}` : ''}</span>
          {['upcoming', 'due', 'missed', 'paused'].includes(state)
            ? <button className="btn sm" style={{ borderColor: r.look.color, color: r.look.color }} onClick={() => { if (state !== 'paused') { try { startRoutine(r.id, run?.n ? { n: run.n } : {}); } catch (e) { console.warn(e); return; } } openRoutineMode(); }}><Icon name="play" size={11} />{state === 'paused' ? 'Back in' : 'Start'}</button>
            : null}
        </div>
      )) : <div className="empty" style={{ padding: '12px 0' }}>None today.</div>}
    </section>
  );
}
